/**
 * Multi-device roles for Nanode sync.
 *
 * Main   — source of truth for Community Detect/scan and sim-local paths.
 *          Only Main may run Community Detect or overwrite the community-scan
 *          inventory. Only Main may transfer the Main role to another device
 *          (except the temporary field-check override — see allowFieldCheck).
 *
 * Secondary — same Nanode account; syncs Inbox / RFQ / schedule / ops changes;
 *             can accept / decline / edit board; MUST NOT run Community Detect
 *             or overwrite Main's detect inventory / scan results.
 */

export type DeviceRole = "main" | "secondary";

/** localStorage key for the stable install-level device UUID. */
export const DEVICE_ID_KEY = "ns.dispatch.deviceId";

/** localStorage key for this device's sync role. */
export const DEVICE_ROLE_KEY = "ns.dispatch.deviceRole";

/**
 * localStorage key for the temporary field-check override.
 *
 * TEMPORARY — for developer field-check only. Will be restricted to
 * Main-only transfer before public release. See releaseNotes.
 */
export const DEVICE_FIELD_CHECK_KEY = "ns.dispatch.fieldCheckClaimMain";

/** Generate a simple RFC 4122 v4-shaped UUID without crypto dependency. */
export function generateDeviceId(): string {
  const hex = "0123456789abcdef";
  let id = "";
  for (let i = 0; i < 32; i++) {
    const rand = Math.floor(Math.random() * 16);
    if (i === 8 || i === 12 || i === 16 || i === 20) id += "-";
    if (i === 12) { id += "4"; continue; }
    if (i === 16) { id += hex[(rand & 0x3) | 0x8]; continue; }
    id += hex[rand];
  }
  return id;
}

export function loadDeviceId(storage?: Pick<Storage, "getItem" | "setItem"> | null): string {
  try {
    const raw = storage?.getItem(DEVICE_ID_KEY);
    if (raw && raw.trim()) return raw.trim();
    const id = generateDeviceId();
    storage?.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    return generateDeviceId();
  }
}

/** Load the saved device role. First run defaults to "main". */
export function loadDeviceRole(storage?: Pick<Storage, "getItem"> | null): DeviceRole {
  try {
    const raw = storage?.getItem(DEVICE_ROLE_KEY);
    if (raw === "main" || raw === "secondary") return raw;
    return "main";
  } catch {
    return "main";
  }
}

export function saveDeviceRole(
  role: DeviceRole,
  storage?: Pick<Storage, "setItem"> | null,
): void {
  try {
    storage?.setItem(DEVICE_ROLE_KEY, role);
  } catch {
    /* ignore quota / private mode */
  }
}

/** Load the temporary field-check override flag. Default OFF. */
export function loadFieldCheckClaimMain(storage?: Pick<Storage, "getItem"> | null): boolean {
  try {
    return storage?.getItem(DEVICE_FIELD_CHECK_KEY) === "1";
  } catch {
    return false;
  }
}

export function saveFieldCheckClaimMain(
  enabled: boolean,
  storage?: Pick<Storage, "setItem"> | null,
): void {
  try {
    storage?.setItem(DEVICE_FIELD_CHECK_KEY, enabled ? "1" : "0");
  } catch {
    /* ignore quota / private mode */
  }
}

export type ClaimMainResult =
  | { ok: true }
  | { ok: false; reason: string };

/** How long a server Main lease stays valid without a renew. */
export const MAIN_LEASE_TTL_MS = 90_000;

/** Server-owned Main lease. Local storage is not the source of truth. */
export type MainLease = {
  deviceId: string;
  name: string;
  acquiredAt: string;
  renewedAt: string;
  expiresAt: string;
};

export function hydrateMainLease(raw: unknown): MainLease | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Partial<MainLease>;
  const deviceId = typeof rec.deviceId === "string" ? rec.deviceId.trim() : "";
  const expiresAt = typeof rec.expiresAt === "string" ? rec.expiresAt.trim() : "";
  if (!deviceId || !expiresAt || Number.isNaN(Date.parse(expiresAt))) return null;
  const name = typeof rec.name === "string" && rec.name.trim() ? rec.name.trim() : deviceId;
  return {
    deviceId,
    name,
    acquiredAt: typeof rec.acquiredAt === "string" && rec.acquiredAt ? rec.acquiredAt : expiresAt,
    renewedAt: typeof rec.renewedAt === "string" && rec.renewedAt ? rec.renewedAt : expiresAt,
    expiresAt,
  };
}

export function mainLeaseActive(lease: MainLease | null | undefined, nowMs: number): boolean {
  if (!lease) return false;
  const exp = Date.parse(lease.expiresAt);
  return Number.isFinite(exp) && exp > nowMs;
}

export function mainLeaseHeldByOther(
  lease: MainLease | null | undefined,
  deviceId: string,
  nowMs: number,
): boolean {
  return Boolean(lease && mainLeaseActive(lease, nowMs) && lease.deviceId !== deviceId);
}

/**
 * The server lease wins while it is unexpired.
 * No lease, or an expired lease, leaves the saved local role alone so an
 * old server that does not publish a lease still works.
 */
export function roleFromMainLease(opts: {
  deviceId: string;
  localRole: DeviceRole;
  lease?: MainLease | null;
  nowMs?: number;
}): DeviceRole {
  const nowMs = opts.nowMs ?? Date.now();
  const lease = opts.lease ?? null;
  if (!mainLeaseActive(lease, nowMs)) return opts.localRole;
  return lease?.deviceId === opts.deviceId ? "main" : "secondary";
}

/**
 * Evaluate whether this device may claim the Main role.
 *
 * Normal rule:   only a device that IS already Main may transfer the role.
 * Field-check:   a Secondary may self-promote when the temporary
 *                "Field-check: claim Main from this device" override is ON,
 *                and only when no other device holds an unexpired Main lease.
 *                This override exists for developer testing while away from
 *                the sim PC and MUST be removed or gated before public release.
 */
export function canClaimMainRole(opts: {
  currentRole: DeviceRole;
  allowFieldCheck: boolean;
  lease?: MainLease | null;
  deviceId?: string;
  nowMs?: number;
}): ClaimMainResult {
  if (
    opts.deviceId &&
    mainLeaseHeldByOther(opts.lease, opts.deviceId, opts.nowMs ?? Date.now())
  ) {
    const holder = opts.lease?.name || "another device";
    return {
      ok: false,
      reason: `Main is leased to ${holder}. Two Mains are not allowed. Wait for that lease to expire, or release it from the device that holds it.`,
    };
  }
  if (opts.currentRole === "main") return { ok: true };
  if (opts.allowFieldCheck) return { ok: true };
  return {
    ok: false,
    reason:
      "Only the current Main device may transfer the Main role. Enable the Field-check override in Advanced Settings to self-promote from this device (developer/field testing only).",
  };
}

/**
 * Whether this device is allowed to run Community Detect / scan and write the
 * local community-scan inventory.
 *
 * Returns true only for Main. Secondary devices MUST NOT run Community Detect
 * or overwrite Main's inventory, even if they share the same Nanode account.
 */
export function isMainDevice(role: DeviceRole): boolean {
  return role === "main";
}

/** Human-readable label for display in Settings and device list. */
export function deviceRoleLabel(role: DeviceRole): string {
  return role === "main" ? "Main" : "Secondary";
}

/** Badge text shown next to the device name in the connected-devices list. */
export function deviceRoleBadge(role: DeviceRole): string {
  return role === "main" ? "Main · source of truth" : "Secondary";
}
